<template>
  <div class="cancel-flow-relevant-books">
    <div class="cancel-flow-relevant-books__header">
      <h2 class="cancel-flow-relevant-books__title">
        These summaries may be relevant to you
      </h2>
      <p class="cancel-flow-relevant-books__subtitle">
        Based on your interests
      </p>
    </div>
    <LoadingSpinner
      v-if="loading"
      class="cancel-flow-relevant-books__loading"
    />
    <ul v-else class="cancel-flow-relevant-books__list">
      <li v-for="book in books" :key="book.id">
        <router-link
          class="cancel-flow-relevant-books__item"
          :to="{
            name: 'book.page',
            params: { url_slug: book.url_slug, position: 1 },
          }"
          @click="onOpenBook"
        >
          <BookCardThumbnail
            class="cancel-flow-relevant-books__cover"
            :doc="book"
          />
          <div class="cancel-flow-relevant-books__info">
            <h3 class="cancel-flow-relevant-books__book-title">
              {{ book.title }}
            </h3>
            <p class="cancel-flow-relevant-books__book-author">
              By {{ book.author }}
            </p>
          </div>
        </router-link>
      </li>
    </ul>
  </div>

  <div class="cancel-flow-popup__footer">
    <AppButton
      :outlined="true"
      class="cancel-flow-popup__cta"
      @click="onRequestSummary"
    >
      Request a summary
    </AppButton>
    <AppButton
      :danger="true"
      :loading="canceling"
      :disabled="canceling"
      class="cancel-flow-popup__cta"
      @click="onEndMembership"
    >
      End my membership
    </AppButton>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'

import AppButton from '@/app/components/button/AppButton.vue'
import BookCardThumbnail from '@/app/components/book/card/BookCardThumbnail.vue'
import LoadingSpinner from '@/components/ui/LoadingSpinner.vue'
import { BillingView } from '@/models/billing'
import type { ReasonValue } from '@/models/billing.cancelFlow'
import { DocSortOption } from '@/models/doc'
import { BooksView } from '@/models/doc.book'
import type { BookListItem } from '@/models/interfaces'
import { backend } from '@/services/backend'
import { Sentry } from '@/services/sentry'
import { Mixpanel } from '@/analytics/mixpanel'

const props = defineProps<{
  billing: BillingView
  canceling: boolean
  reason: ReasonValue | null
}>()
const emit = defineEmits<{
  (e: 'continueCancel'): void
}>()

const router = useRouter()
const books = ref<BookListItem[]>([])
const loading = ref(true)

onMounted(async () => {
  Mixpanel.trackCancelFlowStepShown(props.billing, 'books', props.reason)
  try {
    const [booksData, ownedIds, docsUserData] = await Promise.all([
      backend.getBooksWithRecommendations(),
      backend.getOwnedBookIds(),
      backend.getDocsUserData(),
    ])
    const booksNotSeenBefore = booksData.data.filter((b) => !ownedIds.has(b.id))
    const view = await BooksView.createView(
      { data: booksNotSeenBefore },
      docsUserData,
      DocSortOption.recommended,
      false,
      false,
    )
    books.value = view.docs.slice(0, 5)
  } catch (err) {
    Sentry.captureException(err)
  } finally {
    loading.value = false
  }
})

function trackButtonClick(button: string): void {
  Mixpanel.trackCancelFlowButtonClick(
    props.billing,
    'books',
    button,
    props.reason,
  )
}

function onOpenBook(): void {
  trackButtonClick('open_book')
}

function onRequestSummary(): void {
  trackButtonClick('request_summary')
  router.push({ name: 'help' })
}

function onEndMembership(): void {
  trackButtonClick('end_membership')
  emit('continueCancel')
}
</script>
